import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Badge — pill-shaped status/label indicator.
 *
 * Use the `variant` prop for predefined visual tones, or pass `style` for
 * fully custom colors when wrapping a domain-specific palette (see
 * `feature/StatusBadge`).
 *
 * @example
 *   <Badge variant="default">Neu</Badge>
 *   <Badge variant="success">Aktiv</Badge>
 *   <Badge variant="warning">Pending</Badge>
 *   <Badge variant="danger">Fehler</Badge>
 *   <Badge style={{ background: "#dbeafe", color: "#2563eb" }}>Custom</Badge>
 */
const badgeVariants = cva(
  "inline-flex items-center rounded-pill px-[0.9rem] py-[0.35rem] text-[1.15rem] font-medium leading-none whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-[#f4f4f5] text-[#3f3f46]",
        primary: "bg-light text-foreground",
        success: "bg-[#dcfce7] text-[#16a34a]",
        warning: "bg-[#fef3c7] text-[#d97706]",
        danger: "bg-[#fee2e2] text-[#dc2626]",
        info: "bg-[#dbeafe] text-[#2563eb]",
        neutral: "bg-[#f3f4f6] text-[#6b7280]",
      },
      size: {
        sm: "px-[0.6rem] py-[0.2rem] text-[1.05rem]",
        md: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  },
);

const Badge = React.forwardRef(
  ({ className, variant, size, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Badge.displayName = "Badge";

export { Badge, badgeVariants };
